import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success(t("forgot.codeSent", "If that email exists, a code is on its way."));
      setStep(2);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error(t("forgot.pwMin", "Password must be at least 6 characters"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: newPassword });
      toast.success(t("forgot.resetSuccess", "Password updated. You can sign in now."));
      navigate("/login");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={step === 1 ? t("forgot.title", "Reset your password") : t("forgot.enterCodeTitle", "Enter your code")}
      subtitle={step === 1 ? t("forgot.sub", "Enter your email and we'll send you a 6-digit code.") : t("forgot.enterCodeSub", "We emailed a 6-digit code. Enter it below with your new password.")}
    >
      {step === 1 ? (
        <form onSubmit={requestCode} className="space-y-4">
          <div>
            <Label htmlFor="email">{t("auth.email", "Email")}</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   className="mt-1.5 rounded-xl" data-testid="forgot-email-input" placeholder="you@example.com" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="forgot-send-button">
            {loading ? t("forgot.sending", "Sending…") : t("forgot.sendCode", "Send code")}
          </Button>
        </form>
      ) : (
        <form onSubmit={resetPassword} className="space-y-4">
          <div className="flex items-center gap-2 rounded-2xl bg-sage/10 p-3 text-sm text-sage">
            <MailCheck className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
            {t("forgot.checkEmail", "Check your inbox for the code sent to")} {email}
          </div>
          <div>
            <Label htmlFor="code">{t("forgot.codeLabel", "6-digit code")}</Label>
            <Input id="code" required value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6}
                   className="mt-1.5 rounded-xl tracking-[0.5em] text-center text-lg" data-testid="forgot-code-input" placeholder="000000" />
          </div>
          <div>
            <Label htmlFor="newpw">{t("forgot.newPassword", "New password")}</Label>
            <Input id="newpw" type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                   className="mt-1.5 rounded-xl" data-testid="forgot-newpassword-input" placeholder={t("forgot.pwPh", "At least 6 characters")} />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="forgot-reset-button">
            {loading ? t("forgot.resetting", "Resetting…") : t("forgot.resetBtn", "Reset password")}
          </Button>
          <button type="button" onClick={() => setStep(1)} className="w-full text-center text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-resend-link">
            {t("forgot.resend", "Didn't get it? Send again")}
          </button>
        </form>
      )}
      <Link to="/login" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-primary hover:underline" data-testid="forgot-back-link">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("forgot.backToLogin", "Back to sign in")}
      </Link>
    </AuthShell>
  );
}
